'use client';

import React, { createContext, useContext, useRef } from 'react';
import { motion, useScroll, useMotionValue, useMotionValueEvent } from 'motion/react';

import {
    getGesture,
    getAppliedVariants,
    getVariantsDuration,
    getInitial,
    getVariants,
    getMotionRepeatAnimation,
    getMotionStyle,
    type MotionGesture,
    type MotionEffectType,
    type MotionRange,
    type MotionDuration,
} from './data';

const StaggeringChildrenContext = createContext<[boolean, MotionGesture | null]>([
    false,
    null,
]); // [parentSetStaggeringChildren, gestureOfParent]

interface MotionableBoxProps {
    motionable?: boolean;
    motionGesture: MotionGesture;
    motionEffects: `${MotionEffectType}-${MotionDuration}-${MotionRange}`[];
    motionInViewportOnce?: boolean;
    motionStaggeringChildren?: boolean;
    children: React.ReactNode;
}

export const MotionableBox: React.FC<MotionableBoxProps> = (props) => {
    const {
        motionable,
        motionGesture,
        motionEffects = [],
        motionInViewportOnce = true,
        motionStaggeringChildren = false,
        children,
    } = props;

    const ref = useRef<HTMLDivElement>(null);

    if (!motionable) {
        return children;
    }

    const [parentSetStaggeringChildren, gestureOfParent] = useContext(
        StaggeringChildrenContext
    );

    const CustomComponent = React.forwardRef(
        (
            _props: Omit<{ children: React.ReactNode; [key: string]: unknown }, 'ref'>,
            _ref
        ) => {
            return <div ref={_ref as React.RefObject<HTMLDivElement>} {..._props} />;
        }
    );

    CustomComponent.displayName = 'MotionableBox';

    const MotionBox = motion.create(CustomComponent);

    let gesture = {};

    let inviewportOnce = {};

    let initial = {} as Record<MotionEffectType, unknown>;

    let variants = {} as Record<MotionEffectType, unknown>;

    let transition = {};

    let animate = {} as Record<MotionEffectType, unknown>;

    let motionStyle = {} as Record<MotionEffectType, unknown>;

    if (!parentSetStaggeringChildren || gestureOfParent !== motionGesture) {
        gesture = getGesture(motionGesture);
    }

    if (motionInViewportOnce) {
        inviewportOnce = { viewport: { once: true } };
    }

    // return [motionEffectType, motionDuration, motionRange]
    const parsedMotionEffects = motionEffects.map((motionEffect) => {
        return motionEffect.split('-') as [MotionEffectType, MotionDuration, MotionRange];
    });

    const appliedVariants = getAppliedVariants(parsedMotionEffects);

    const variantsDuration = getVariantsDuration(parsedMotionEffects);

    if (motionGesture === 'scroll') {
        const { scrollY } = useScroll({
            target: ref,
            offset: ['start end', 'end 0.1'],
        });

        const scrollYProgress = useMotionValue(0);

        useMotionValueEvent(scrollY, 'change', (latest) => {
            if (motionGesture === 'scroll' && ref.current) {
                const rect = ref.current.getBoundingClientRect();

                const elementTop = rect.top + window.scrollY;

                const elementHeight = rect.height;

                const viewportHeight = window.innerHeight;

                const start = elementTop - viewportHeight;

                const end = elementTop + elementHeight;

                const progress = (latest - start) / (end - start);

                const clampedProgress = Math.max(0, Math.min(1, progress));

                scrollYProgress.set(clampedProgress);
            }
        });

        motionStyle = getMotionStyle(parsedMotionEffects, scrollYProgress);
    } else if (motionGesture === 'repeat') {
        const { animate: repeatAnimation, transition: repeatTransition } =
            getMotionRepeatAnimation(parsedMotionEffects);

        animate = repeatAnimation;

        transition = repeatTransition;
    } else {
        initial = getInitial(motionGesture, appliedVariants);

        variants = getVariants(
            motionGesture,
            appliedVariants,
            variantsDuration,
            motionStaggeringChildren
        );
    }

    return (
        <StaggeringChildrenContext.Provider
            value={[motionStaggeringChildren, motionGesture]}
        >
            <MotionBox
                ref={ref}
                variants={variants}
                initial={initial}
                transition={transition}
                style={motionStyle}
                animate={animate}
                {...gesture}
                {...inviewportOnce}
            >
                {children}
            </MotionBox>
        </StaggeringChildrenContext.Provider>
    );
};
